import { normalizePassageText } from './passageText';
import { countPassageWords } from '@/constants/passage';
import {
  detectLanguageGuardViolation,
  formatLanguageGuardFeedback,
  normalizePassageDashes,
  type LanguageGuardViolation,
} from './languageGuard';

export const MAX_PASSAGE_ID_LENGTH = 50;

/**
 * "//" separates the passage from the question type in the composite ID of
 * question-ordered rows ("17-1 // 요지_Normal"), and exports split on it, so a
 * user may not type it into a passage number. Mirrors the server's
 * passage_id_has_forbidden_separator (workbook_merge_service); the shared
 * fixture my-flask-app/tests/fixtures/workbook_merge_mirror_cases.json pins both.
 */
export const PASSAGE_ID_SEPARATOR_MESSAGE = "지문 번호에는 '//'를 넣을 수 없습니다.";
const COMPOSITE_SEPARATOR = ' // ';
const SEPARATOR_MARKER = '//';
const COMPOSITE_TYPE_PART = /^[^/]+_(?:Normal|Hard|Easy|Medium)$/;
// Python's str.strip() set (str.isspace), which the server's rule trims with.
// JS trim() differs at the edges: it keeps \x1c-\x1f and \x85, and strips \ufeff.
const PYTHON_SPACE = String.raw`\t\n\v\f\r\x1c-\x1f \x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000`;
const PYTHON_STRIP = new RegExp(`^[${PYTHON_SPACE}]+|[${PYTHON_SPACE}]+$`, 'g');
const PYTHON_SPACE_RUN = new RegExp(`[${PYTHON_SPACE}]+`, 'g');
/** Python's str.strip(): the trim a passage number gets before the server compares or stores it. */
export const pythonStrip = (value: string) => value.replace(PYTHON_STRIP, '');
/**
 * The text the server compares a passage number as (api/sheets.py
 * _same_passage_id): runs of whitespace collapsed to one space, then stripped.
 */
export const comparablePassageId = (value: string) => pythonStrip(value.replace(PYTHON_SPACE_RUN, ' '));
/** True when ``next`` is the stored passage number: a legacy "//" it carries is not re-judged. */
export function isSamePassageId(stored: string | null | undefined, next: string | null | undefined): boolean {
  return comparablePassageId(stored ?? '') === comparablePassageId(next ?? '');
}
/** Mock-test and past-set sheets keep an exam's question order (server: SPECIAL_SHEET_PREFIXES). */
export const QUESTION_ORDERED_SHEET_PREFIXES = ['실전모의고사', '기출문제세트'] as const;

/** True for a well-formed "<passage> // <type>_<difficulty>" ID. */
export function isValidCompositePassageId(value: string): boolean {
  const text = pythonStrip(value);
  const at = text.indexOf(COMPOSITE_SEPARATOR);
  if (at === -1) return false;
  const head = text.slice(0, at);
  const tail = text.slice(at + COMPOSITE_SEPARATOR.length);
  return pythonStrip(head) !== '' && !head.includes(SEPARATOR_MARKER) && COMPOSITE_TYPE_PART.test(tail);
}

/** True when a user-entered passage number holds a "//" it may not hold. */
export function passageIdHasForbiddenSeparator(
  value: unknown,
  { allowComposite }: { allowComposite: boolean },
): boolean {
  if (typeof value !== 'string' || !value.includes(SEPARATOR_MARKER)) return false;
  return !(allowComposite && isValidCompositePassageId(value));
}

/** A sheet whose rows keep an exam's question order: by name, or because it holds composite IDs. */
export function isQuestionOrderedSheet(name: string, passageIds: Array<string | null | undefined>): boolean {
  return QUESTION_ORDERED_SHEET_PREFIXES.some((prefix) => name.startsWith(prefix))
    || passageIds.some((passageId) => typeof passageId === 'string' && passageId.includes(COMPOSITE_SEPARATOR));
}

/**
 * The message the server would answer for writing ``nextRows`` over
 * ``storedRows``, or null. Only IDs new compared with the stored rows are
 * judged, counting occurrences, so reorder / delete / paste keep a stored
 * legacy "//" ID while a copy of it or a changed one is refused.
 */
export function sheetRowsSeparatorError(
  sheetName: string,
  storedRows: Array<{ passage_id?: string | null }>,
  nextRows: Array<{ passage_id?: string | null }>,
): string | null {
  const storedIds = storedRows.map((row) => row.passage_id);
  const allowComposite = isQuestionOrderedSheet(sheetName, storedIds);
  const remaining = new Map<string, number>();
  storedIds.forEach((passageId) => {
    if (typeof passageId === 'string') remaining.set(passageId, (remaining.get(passageId) ?? 0) + 1);
  });
  for (const { passage_id: passageId } of nextRows) {
    const left = typeof passageId === 'string' ? remaining.get(passageId) ?? 0 : 0;
    if (left > 0) {
      remaining.set(passageId as string, left - 1);
      continue;
    }
    if (passageIdHasForbiddenSeparator(passageId, { allowComposite })) return PASSAGE_ID_SEPARATOR_MESSAGE;
  }
  return null;
}

export interface ValidatePassageInputParams {
  passageId: string;
  /** The stored passage number when editing; an unchanged one is not re-judged for "//". */
  originalPassageId?: string | null;
  passage: string;
  translation?: string;
  minWords: number;
  maxWords: number;
}

export interface PassageInputValidationResult {
  normalizedPassageId: string;
  normalizedPassage: string;
  normalizedTranslation: string;
  passageIdLength: number;
  wordCount: number;
  languageViolation: LanguageGuardViolation | null;
  errors: {
    passageIdRequired: boolean;
    passageIdTooLong: boolean;
    passageIdSeparator: boolean;
    passageRequired: boolean;
    passageTooShort: boolean;
    passageTooLong: boolean;
    passageLanguageGuard: boolean;
  };
  isValid: boolean;
}

export function validatePassageInput({
  passageId,
  originalPassageId,
  passage,
  translation = '',
  minWords,
  maxWords,
}: ValidatePassageInputParams): PassageInputValidationResult {
  // Stripped as the server strips it, so the edges (\x1c-\x1f, \x85, \ufeff) agree.
  const normalizedPassageId = pythonStrip(normalizePassageText(passageId));
  const normalizedPassage = normalizePassageDashes(normalizePassageText(passage)).trim();
  const normalizedTranslation = normalizePassageText(translation).trim();
  const passageIdLength = normalizedPassageId.length;
  const wordCount = normalizedPassage ? countPassageWords(normalizedPassage) : 0;
  const languageViolation = detectLanguageGuardViolation(normalizedPassage);

  const errors = {
    passageIdRequired: normalizedPassageId.length === 0,
    passageIdTooLong: passageIdLength > MAX_PASSAGE_ID_LENGTH,
    // The library holds plain passage numbers: no composite ID either.
    passageIdSeparator: !isSamePassageId(normalizePassageText(originalPassageId ?? ''), normalizedPassageId)
      && passageIdHasForbiddenSeparator(normalizedPassageId, { allowComposite: false }),
    passageRequired: normalizedPassage.length === 0,
    passageTooShort: normalizedPassage.length > 0 && wordCount < minWords,
    passageTooLong: normalizedPassage.length > 0 && wordCount > maxWords,
    passageLanguageGuard: languageViolation !== null,
  };

  return {
    normalizedPassageId,
    normalizedPassage,
    normalizedTranslation,
    passageIdLength,
    wordCount,
    languageViolation,
    errors,
    isValid: Object.values(errors).every((value) => value === false),
  };
}

export function summarizePassageValidationError(
  result: PassageInputValidationResult,
  minWords: number,
  maxWords: number,
  prefix?: string
): string[] {
  const messages: string[] = [];
  const label = prefix ? `${prefix}: ` : '';

  if (result.errors.passageIdRequired) {
    messages.push(`${label}지문번호를 입력해주세요.`);
  }

  if (result.errors.passageIdTooLong) {
    messages.push(
      `${label}지문번호는 ${MAX_PASSAGE_ID_LENGTH}자 이하로 입력해주세요. (현재 ${result.passageIdLength}자)`
    );
  }

  if (result.errors.passageIdSeparator) {
    messages.push(`${label}${PASSAGE_ID_SEPARATOR_MESSAGE}`);
  }

  if (result.errors.passageRequired) {
    messages.push(`${label}지문을 입력해주세요.`);
  }

  if (result.errors.passageTooShort) {
    messages.push(
      `${label}지문이 너무 짧습니다. (${minWords}단어 이상 필요, 현재 ${result.wordCount}단어)`
    );
  }

  if (result.errors.passageTooLong) {
    messages.push(
      `${label}지문이 너무 깁니다. (${maxWords.toLocaleString()}단어 이하 필요, 현재 ${result.wordCount}단어)`
    );
  }

  if (result.errors.passageLanguageGuard && result.languageViolation) {
    messages.push(formatLanguageGuardFeedback(result.languageViolation, `${label}지문`));
  }

  return messages;
}

export function summarizeValidationMessages(messages: string[], limit: number = 3): string {
  if (messages.length === 0) {
    return '입력 조건을 확인해주세요.';
  }

  const visible = messages.slice(0, limit);
  const hiddenCount = messages.length - visible.length;

  if (hiddenCount <= 0) {
    return visible.join(' / ');
  }

  return `${visible.join(' / ')} / 외 ${hiddenCount}건`;
}
